import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  Clock,
  Download,
  HelpCircle,
  Plus,
  UserMinus,
  Users,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/ui/MetricTile';
import { Select } from '@/components/ui/Select';
import type { Option } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { teacherApi } from '../api/teacher.api';
import type { AllocatedClass } from '../types/teacher';
import { allotmentLabel } from '../utils';
import { defaultReportPeriod } from '../constants/attendanceReport';
import { StudentAttendanceRegister } from './attendance/StudentAttendanceRegister';
import { TeacherPunchInCard } from './attendance/TeacherPunchInCard';
import { MyLeavePanel } from './attendance/MyLeavePanel';
import { TeacherAttendanceReportsView } from './attendance/TeacherAttendanceReportsView';
import type { AttendanceRegister } from '@/types/attendance';
import { cn } from '@/lib/utils';

interface TeacherAttendanceSectionProps {
  currentClass: AllocatedClass;
  allocatedClasses?: AllocatedClass[];
  selectedClassId?: string;
  onSelectClass?: (id: string) => void;
}

type SubTab = 'attendance' | 'reports' | 'leave';

/** Whether a `?tab=` value names one of the views — a link may say anything. */
function isSubTab(value: string | null): value is SubTab {
  return SUB_TABS.some((one) => one.key === value);
}

/**
 * The page's three views. My leave sits here, beside the punch-in card, because
 * it is the teacher's own record of being at work — and it is where the
 * office's decision on a leave request sends them (`/teacher/attendance`).
 */
const SUB_TABS: { key: SubTab; label: string; title: string; description: string }[] = [
  {
    key: 'attendance',
    label: 'Attendance',
    title: 'Daily Attendance',
    description: 'Mark and manage student attendance for your classes.',
  },
  {
    key: 'reports',
    label: 'Reports',
    title: 'Attendance Reports',
    description: 'View and analyze attendance data with detailed reports.',
  },
  {
    key: 'leave',
    label: 'My leave',
    title: 'My Leave',
    description: 'Apply for leave, see your balance, and follow what the school office decided.',
  },
];

export function TeacherAttendanceSection({
  currentClass,
  allocatedClasses = [],
  selectedClassId = '',
  onSelectClass,
}: TeacherAttendanceSectionProps) {
  // In the address (`?tab=reports`), so a refresh keeps the view the teacher
  // was on rather than dropping them back on the register.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedTab = searchParams.get('tab');
  const activeSubTab: SubTab = isSubTab(linkedTab) ? linkedTab : 'attendance';
  function setActiveSubTab(next: SubTab) {
    setSearchParams(
      (current) => {
        current.set('tab', next);
        return current;
      },
      { replace: true },
    );
  }
  const tab = SUB_TABS.find((one) => one.key === activeSubTab) ?? SUB_TABS[0];
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isApplyLeaveOpen, setIsApplyLeaveOpen] = useState(false);
  const [leaveYearUuid, setLeaveYearUuid] = useState('');
  const [leaveAcademicYears, setLeaveAcademicYears] = useState<{ uuid: string; name: string; isCurrent: boolean }[]>([]);
  /**
   * The window the Reports tab totals.
   *
   * Held here rather than inside the report because it is a header control, the
   * same place the Attendance tab puts the day it is showing — one row of
   * chrome that changes what it offers, not two rows that disagree.
   */
  const [period, setPeriod] = useState(defaultReportPeriod);
  /**
   * Bumped by Clear to start the report afresh. The report type and search box
   * live inside the report, so a new key is how the header resets them too,
   * without lifting two more pieces of state up here.
   */
  const [reportResetKey, setReportResetKey] = useState(0);
  const exportFnRef = useRef<(() => void) | null>(null);
  const [canExport, setCanExport] = useState(false);
  const [isReportViewFiltered, setIsReportViewFiltered] = useState(false);
  const handleRegisterExport = useCallback((fn: (() => void) | null, available: boolean) => {
    exportFnRef.current = fn;
    setCanExport(available);
  }, []);

  /**
   * The classes the Reports tab offers: only those this teacher is class
   * teacher of. A report is the class teacher's account of their own class —
   * a subject teacher's period with 8-B is not theirs to report on.
   */
  const reportClasses = useMemo(
    () => allocatedClasses.filter((ac) => ac.role === 'Class Teacher'),
    [allocatedClasses],
  );
  /** The class on show in Reports — the one picked, if it is one of theirs, else their first. */
  const reportClass =
    reportClasses.find((ac) => ac.id === currentClass.id) ?? reportClasses[0] ?? null;

  /** Back to this month and an unfiltered report (keeps the chosen class). */
  const defaultPeriod = useMemo(() => defaultReportPeriod(), []);
  const isDateFiltered = period.from !== defaultPeriod.from || period.to !== defaultPeriod.to;
  const isFilterApplied = isDateFiltered || isReportViewFiltered;

  function clearReportFilters() {
    setPeriod(defaultReportPeriod());
    setIsReportViewFiltered(false);
    setReportResetKey((key) => key + 1);
  }

  const sectionUuid = currentClass.classSectionUuid;

  /**
   * The register for the chosen class and day, fetched once here.
   *
   * The five cards below and the table they sit above are the same rows, so it
   * is read once and handed down rather than fetched by each — two requests for
   * one roster, able to disagree while one was still in flight.
   */
  const register = useApiQuery<AttendanceRegister | null>(
    () =>
      sectionUuid
        ? teacherApi.register(sectionUuid, selectedDate || undefined)
        : Promise.resolve(null),
    [sectionUuid, selectedDate],
  );
  const { data: registerData, isLoading: isLoadingRegister } = register;

  const classOptions = useMemo<Option[]>(
    () =>
      allocatedClasses.map((ac) => ({
        value: ac.id,
        label: allotmentLabel(ac),
      })),
    [allocatedClasses],
  );

  const students = registerData?.students ?? [];
  const totalStudents = students.length;

  const presentCount = useMemo(
    () => students.filter((s) => s.status === 'PRESENT').length,
    [students],
  );
  const absentCount = useMemo(
    () => students.filter((s) => s.status === 'ABSENT').length,
    [students],
  );
  const lateCount = useMemo(
    () => students.filter((s) => s.status === 'LATE').length,
    [students],
  );
  // A child on leave has been accounted for — counted here so "Not Marked"
  // holds only the children nobody has marked yet.
  const leaveCount = useMemo(
    () => students.filter((s) => s.status === 'LEAVE').length,
    [students],
  );
  const notMarkedCount = useMemo(
    () => students.filter((s) => s.status === null).length,
    [students],
  );

  const formatRate = (count: number, total: number) => {
    if (total <= 0) return '0';
    const pct = (count / total) * 100;
    return Number(pct.toFixed(2)).toString();
  };

  const presentRate = formatRate(presentCount, totalStudents);
  const absentRate = formatRate(absentCount, totalStudents);
  const lateRate = formatRate(lateCount, totalStudents);
  const notMarkedRate = formatRate(notMarkedCount, totalStudents);

  /**
   * Nothing has arrived yet.
   *
   * Only the very first load — changing the date keeps the previous register on
   * screen until the new one lands rather than blanking all five cards between.
   */
  const isFirstLoad = isLoadingRegister && !registerData;
  /** What a figure reads as before it is known. Never a nought, which is an answer. */
  const count = (value: number) => (isFirstLoad ? '—' : String(value));
  /** Undefined rather than an empty string, so the tile drops the line instead
   *  of reserving a blank one under the dash. */
  const rate = (value: string) => (isFirstLoad ? undefined : `${value}%`);

  return (
    <div className="flex flex-col gap-5">
      {/* --- PAGE HEADER & SUB-TABS ROW --------------------------------------- */}
      <div className="flex flex-col gap-3.5">
        {/* Top Header Row: Title on Left, Class & Date Selectors on Right */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-ink tracking-tight">{tab.title}</h1>
            <p className="text-xs sm:text-sm text-ink-3 font-medium ">{tab.description}</p>
          </div>

          {/* Right Header Controls: Class Selector & Date Picker on Attendance/Reports,
              or Apply for Leave button when My Leave tab is active */}
          {activeSubTab !== 'leave' ? (
            <div
              className={cn(
                'grid grid-cols-1 gap-2 sm:flex sm:gap-2.5 w-full sm:w-auto',
                // The report's dates carry visible labels; the other controls line
                // up with the boxes underneath them, not with the labels.
                activeSubTab === 'attendance' ? 'sm:items-center' : 'sm:items-end',
              )}
            >
              {activeSubTab === 'attendance' ? (
                <>
                  {classOptions.length > 0 && onSelectClass && (
                    <div className="w-full sm:w-52">
                      <Select
                        label="Select Class"
                        hideLabel
                        options={classOptions}
                        value={selectedClassId || currentClass.id}
                        onChange={(e) => onSelectClass(e.target.value)}
                        className="h-9 w-full sm:w-52 text-[14px] font-semibold rounded-field"
                      />
                    </div>
                  )}

                  <div className="w-full sm:w-44">
                    <DatePicker
                      label="Select Date"
                      hideLabel
                      size="sm"
                      value={selectedDate || registerData?.date || ''}
                      max={registerData?.date}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="h-9 w-full sm:w-44 text-[14px] font-semibold rounded-field"
                    />
                  </div>
                </>
              ) : (
                /* A report covers a window, so the header offers both ends of it.
                   Each end bounds the other, so the period can never be typed
                   backwards. */
                <div className="grid grid-cols-2 items-end gap-2 sm:flex sm:gap-2.5">
                  {reportClass && (
                    <div className="col-span-2 sm:col-span-1 h-9 flex items-center">
                      <span
                        title={`${reportClass.className} (${reportClass.role})`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 text-xs sm:text-[13px] font-bold text-emerald-700 dark:text-emerald-400 shadow-2xs whitespace-nowrap"
                      >
                        <Users className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        {reportClass.className} ({reportClass.role})
                      </span>
                    </div>
                  )}
                  <DatePicker
                    label="From"
                    size="sm"
                    value={period.from}
                    max={period.to}
                    onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                    className="h-9 w-full text-[12px] font-semibold rounded-field sm:w-40"
                  />
                  <DatePicker
                    label="To"
                    size="sm"
                    value={period.to}
                    min={period.from}
                    max={registerData?.date}
                    onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                    className="h-9 w-full text-[12px] font-semibold rounded-field sm:w-40"
                  />
                  <Button
                    variant={isFilterApplied ? 'dangerSoft' : 'secondary'}
                    size="sm"
                    disabled={!isFilterApplied}
                    leftIcon={
                      <X
                        className={cn(
                          'h-3.5 w-3.5 transition-colors',
                          isFilterApplied ? 'text-rose-600 dark:text-rose-400' : 'text-ink-3 opacity-60',
                        )}
                      />
                    }
                    onClick={clearReportFilters}
                    className={cn(
                      'col-span-1 h-9 px-2.5 text-xs font-semibold sm:col-span-1 transition-colors',
                      isFilterApplied
                        ? 'border-rose-500/40 bg-rose-500/10 text-rose-600 hover:border-rose-500 hover:bg-rose-500/20 hover:text-rose-700 dark:border-rose-500/40 dark:text-rose-400 dark:hover:bg-rose-500/20'
                        : 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-3.5 w-3.5" />}
                    onClick={() => exportFnRef.current?.()}
                    disabled={!canExport}
                    className="col-span-1 h-9 px-3 text-xs sm:text-[13px] font-semibold sm:col-span-1 whitespace-nowrap"
                  >
                    Download Report
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto">
              {leaveAcademicYears.length > 0 && (
                <div className="w-full sm:w-52">
                  <Select
                    label="Academic year"
                    hideLabel
                    options={leaveAcademicYears.map((year) => ({
                      value: year.isCurrent ? '' : year.uuid,
                      label: year.isCurrent ? `${year.name} (current)` : year.name,
                    }))}
                    value={leaveYearUuid}
                    onChange={(e) => setLeaveYearUuid(e.target.value)}
                    className="h-9 w-full sm:w-52 text-[14px] font-semibold rounded-field"
                  />
                </div>
              )}
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setIsApplyLeaveOpen(true)}
                className="h-9 px-4 text-[14px] font-bold shadow-xs shrink-0 self-start sm:self-auto"
              >
                Apply for leave
              </Button>
            </div>
          )}
        </div>

        {/* Sub-Navigation Tabs */}
        <div className="flex items-center gap-8 border-b border-line">
          {SUB_TABS.map((one) => (
            <button
              key={one.key}
              type="button"
              onClick={() => setActiveSubTab(one.key)}
              aria-current={activeSubTab === one.key ? 'page' : undefined}
              className={cn(
                'pb-2.5 text-[15px] font-bold transition-all relative cursor-pointer',
                activeSubTab === one.key
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-ink-3 hover:text-ink',
              )}
            >
              {one.label}
              {activeSubTab === one.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 dark:bg-emerald-400 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* --- TAB 1: ATTENDANCE ------------------------------------------------ */}
      {activeSubTab === 'attendance' && (
        <div className="flex flex-col gap-5">
          {/*
            The day's five figures.

            `MetricTile` rather than five hand-drawn boxes: these were copies of
            it down to the tint, the icon plate and the type scale, so a change
            to the tile had to be made five times here and once in the Reports
            tab next door (CLAUDE.md §3). The copies also carried `h-4.5`, which
            is not on Tailwind 3.4's scale and compiles to nothing — their icons
            had been rendering at lucide's default 24px.
          */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3.5">
            <MetricTile
              tone="blue"
              icon={<Users className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Total Students"
              value={count(totalStudents)}
              hint={leaveCount > 0 ? `${leaveCount} on leave` : 'All Students'}
            />
            <MetricTile
              tone="emerald"
              icon={<Check className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Present"
              value={count(presentCount)}
              hint={rate(presentRate)}
            />
            <MetricTile
              tone="rose"
              icon={<UserMinus className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Absent"
              value={count(absentCount)}
              hint={rate(absentRate)}
            />
            <MetricTile
              tone="amber"
              icon={<Clock className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Late"
              value={count(lateCount)}
              hint={rate(lateRate)}
            />
            {/* Spans both columns on a phone, so the last of five does not sit
                beside an empty one. */}
            <MetricTile
              tone="indigo"
              icon={<HelpCircle className="h-4 w-4 sm:h-5 sm:w-5" />}
              label="Not Marked"
              value={count(notMarkedCount)}
              hint={rate(notMarkedRate)}
              className="col-span-2 sm:col-span-1"
            />
          </div>

          {/* 2-COLUMN MAIN CONTENT: Wider Left Column + Compact Right Sidebar */}
          <div className="flex flex-col xl:flex-row items-start gap-5">
            {/* LEFT COLUMN: Student Daily Register Table (Flex 1 to take all remaining width) */}
            <div className="flex-1 min-w-0 w-full">
              {allocatedClasses.length === 0 ? (
                <div className="card p-6">
                  <EmptyState
                    icon={<Users className="h-5 w-5" />}
                    title="No classes allotted to you yet"
                    description="Your institute office arranges who teaches what. Once a section and subject are put against your name, your student register will appear here."
                  />
                </div>
              ) : (
                <StudentAttendanceRegister register={register} />
              )}
            </div>

            {/* RIGHT COLUMN: Teacher Daily Punch-In Card (Fixed width 320px on xl) */}
            <div className="w-full xl:w-[320px] 2xl:w-[340px] shrink-0">
              <TeacherPunchInCard />
            </div>
          </div>
        </div>
      )}

      {/* --- TAB 3: MY LEAVE -------------------------------------------------- */}
      {activeSubTab === 'leave' && (
        <MyLeavePanel
          isFormOpen={isApplyLeaveOpen}
          onFormOpenChange={setIsApplyLeaveOpen}
          yearUuid={leaveYearUuid}
          onYearChange={setLeaveYearUuid}
          onAcademicYearsLoaded={setLeaveAcademicYears}
        />
      )}

      {/* --- TAB 2: REPORTS --------------------------------------------------- */}
      {activeSubTab === 'reports' && (
        allocatedClasses.length === 0 ? (
          <div className="card p-6">
            <EmptyState
              icon={<Users className="h-5 w-5" />}
              title="No classes allotted to you yet"
              description="Your institute office arranges who teaches what. Once a section and subject are put against your name, class attendance reports will appear here."
            />
          </div>
        ) : !reportClass ? (
          <div className="card p-6">
            <EmptyState
              icon={<Users className="h-5 w-5" />}
              title="Reports are for class teachers"
              description="You are not the class teacher of any class this year. Attendance reports appear here for the class your school office makes you class teacher of."
            />
          </div>
        ) : (
          <TeacherAttendanceReportsView
            key={reportResetKey}
            currentClass={reportClass}
            from={period.from}
            to={period.to}
            onRegisterExport={handleRegisterExport}
            onFilterStateChange={setIsReportViewFiltered}
          />
        )
      )}
    </div>
  );
}
